import React, { useEffect, useRef } from "react";
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/translations';
import "./PequenaDescricaoSection.css";
import descricao1 from "../../assets/descricao1.jpg";
import descricao2 from "../../assets/descricao2.jpg";

export default function PequenaDescricao() {
    const { language } = useLanguage();
    const t = translations?.[language] || translations?.pt || {};
    const leftImgRef = useRef(null);
    const rightImgRef = useRef(null);
    const textRefs = useRef([]);

    // IntersectionObserver to reveal text and images when entering viewport
    useEffect(() => {
        const nodes = [...document.querySelectorAll(".descricao-texto, .descricao-imagem")];

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("visible");
                    }
                });
            },
            { threshold: 0.18 }
        );

        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, []);

    // Move the small label into the image wrapper on mobile so it can sit on top of the image
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 920px)');
        const moved = [];

        const move = () => {
            document.querySelectorAll('.descricao-item').forEach((item) => {
                const subtitle = item.querySelector('.subtitulo');
                const imageWrap = item.querySelector('.descricao-imagem-wrap');
                if (!subtitle || !imageWrap) return;

                // already moved
                if (imageWrap.contains(subtitle)) return;

                // remember original parent and nextSibling to restore later
                moved.push({ subtitle, originalParent: subtitle.parentNode, nextSibling: subtitle.nextSibling });
                imageWrap.appendChild(subtitle);
                subtitle.classList.add('subtitulo--over-image');
            });
        };

        const restore = () => {
            moved.forEach(({ subtitle, originalParent, nextSibling }) => {
                if (originalParent) originalParent.insertBefore(subtitle, nextSibling);
                subtitle.classList.remove('subtitulo--over-image');
            });
            moved.length = 0;
        };

        const handle = (e) => {
            if (e.matches) move();
            else restore();
        };

        // initial
        if (mq.matches) move();

        if (mq.addEventListener) mq.addEventListener('change', handle);
        else mq.addListener(handle);

        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handle);
            else mq.removeListener(handle);
            restore();
        };
    }, []);

    // gentle parallax depending on element position (not raw scrollY)
    useEffect(() => {
        const handle = () => {
            const winCenter = window.innerHeight / 2 + window.scrollY;

            const moveImage = (el, direction = 1, strength = 0.06) => {
                if (!el) return;
                const r = el.getBoundingClientRect();
                const elCenter = r.top + window.scrollY + r.height / 2;
                const delta = (winCenter - elCenter) * strength * direction;
                // clamp small values
                el.style.transform = `translateY(${Math.max(-40, Math.min(40, delta))}px)`;
            };

            moveImage(rightImgRef.current, -1, 0.06); // right image moves up slightly
            moveImage(leftImgRef.current, 1, 0.06);   // left image moves down slightly
        };

        handle(); // run once
        window.addEventListener("scroll", handle, { passive: true });
        window.addEventListener("resize", handle);
        return () => {
            window.removeEventListener("scroll", handle);
            window.removeEventListener("resize", handle);
        };
    }, []);

    return (
        <section className="pequena-descricao" id="gardens-heritage">
            <div className="descricao-container">
                {/* BLOCK 1: text left, image right */}
                <div className="descricao-item">
                    <div
                        className="descricao-texto"
                        ref={(el) => (textRefs.current[0] = el)}
                    >
                        <h3 className="subtitulo">{t.gardens.section1.subtitle}</h3>
                        <h2 className="titulo">{t.gardens.section1.title}</h2>
                        <p className="lead">
                            {t.gardens.section1.lead}
                        </p>
                        <p className="body">
                            {t.gardens.section1.body}
                        </p>
                    </div>

                    <div className="descricao-imagem-wrap">
                        <img
                            ref={rightImgRef}
                            className="descricao-imagem"
                            src={descricao1}
                            alt="Historic gardens"
                            loading="lazy"
                        />
                    </div>
                </div>

                {/* BLOCK 2: image left, text right */}
                <div className="descricao-item reverse">
                    <div className="descricao-imagem-wrap">
                        <img
                            ref={leftImgRef}
                            className="descricao-imagem"
                            src={descricao2}
                            alt="Botanical park"
                            loading="lazy"
                        />
                    </div>

                    <div
                        className="descricao-texto"
                        ref={(el) => (textRefs.current[1] = el)}
                    >
                        <h2 className="titulo">{t.gardens.section2.title}</h2>
                        <p className="lead">
                            {t.gardens.section2.lead}
                        </p>
                        <p className="body">
                            {t.gardens.section2.body}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
